import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { motion } from "framer-motion";
import { toggleStateValue } from "@/store/app/app.slice";
import HeaderSearch from "./HeaderSearch";
import { RootState, store } from "../../store";
import SlikeLogo from "../../assets/svg/slikelogo.svg?react";
import AppIcon from "../../assets/icons/apps-alt.svg?react";
import Dropdown from "react-bootstrap/Dropdown";
import ProfileIcon from "../../assets/svg/profile.svg?react";
import { connect, ConnectedProps } from "react-redux";
import { refreshCurrentUser } from "@/store/user/user.action";
import { useClickOutside } from "../../utils";
import { FileUploader } from "../Create/Create";
import Modal from "../Modal/Modal";
import Notifications from "../Notifications/Notifications";
import { Tooltip } from "..";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  emptyAssetDetails,
  emptyMediaDetails,
} from "@/store/media/media.slice";
import { getMedia } from "@/store/media/media.action";
import {
  Brain,
  Video,
  Tv,
  BarChart3,
  FileVideo,
  Compass,
  Settings,
  Bell,
  TvMinimalPlay,
  RotateCw,
  Binoculars,
  Archive,
  CircleUser,
  LogOut,
} from "lucide-react";
import { readNotification } from "@/store/app/app.actions";
import { useNotificationObserver } from "../../hooks/useNotificationObserver";
import {
  updateIsClearFilter,
  updateIsFilter,
} from "@/store/media/media.action";
import { checkCMSPermission } from "@/utils/permission";
import { APP_LAUNCHER_CONFIG } from "@/utils/constants/uiConstants";
import DenmarkPriorityModal from "../MediaPublish/DenmarkPublish/DenmarkPriorityModal";
import DenmarkFavIcon from "../../assets/icons/svg/denmark_fav.svg?react";
import { getTrackUserContext, trackEvent } from "@/utils/analytics";
import { getContentLocation } from "@/utils/utilFunctions/commmonFunctions";

// Type definitions
interface Notification {
  id: string;
  read: number;
  [key: string]: unknown;
}

// App Launcher Configuration

const Header = ({
  toggleStateValue,
  emptyAssetDetails,
  emptyMediaDetails,
  currentMediaDetailsForEdit,
  singleCurrentUser,
  getMedia,
  notifications,
  readNotification,
  updateIsClearFilter,
  updateIsFilter,
  refreshCurrentUser,
  bunchProductList,
  defaultTeam,
}: Props) => {
  const notificationRef = useRef<HTMLDivElement>(null);
  const notificationDropdownRef = useRef<HTMLDivElement>(null);
  const deeplinkProcessedRef = useRef<boolean>(false);
  const [showNotificationDropdown, setShowNotificationDropdown] =
    useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [denmarkPriorityOpen, setDenmarkPriorityOpen] = useState(false);
  const [notificationLoading, setNotificationLoading] =
    useState<boolean>(false);
  const [initialNotificationTab, setInitialNotificationTab] = useState<
    number | undefined
  >(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const analyticsLocation = getContentLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Memoize expensive computations
  const unreadCount = useMemo(() => {
    return notifications?.filter((n: Notification) => n.read === 0).length ?? 0;
  }, [notifications]);

  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);
  const hasCMSAccess = useMemo(
    () =>
      checkCMSPermission(
        singleCurrentUser?.permissions,
        singleCurrentUser?.team
      ),
    [singleCurrentUser?.permissions, singleCurrentUser?.team]
  );

  const hasDenmarkAccess = defaultTeam?.settings?.host !== "none";

  useEffect(() => {
    if (!notificationLoading && unreadCount !== previousUnreadCount) {
      setPreviousUnreadCount(unreadCount);
    }
  }, [unreadCount, notificationLoading, previousUnreadCount]);

  const displayCount = notificationLoading ? previousUnreadCount : unreadCount;

  // Handle deeplink to open notification center with Requests tab
  useEffect(() => {
    if (deeplinkProcessedRef.current) return;

    const hasNotificationRequestsParam =
      searchParams.has("notificationrequests") ||
      new URLSearchParams(location.search).has("notificationrequests");

    // Only proceed if we have the parameter, user is loaded, and modal is not already open
    if (hasNotificationRequestsParam && singleCurrentUser && !modalOpen) {
      deeplinkProcessedRef.current = true;
      setInitialNotificationTab(2); // Set to Requests tab (index 2)
      setModalOpen(true);
      // Clean up the URL parameter after opening
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("notificationrequests");
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [
    location.search,
    singleCurrentUser,
    modalOpen,
    searchParams,
    setSearchParams,
  ]);

  useNotificationObserver({
    notifications,
    user: singleCurrentUser,
    readNotification,
    isActive: showNotificationDropdown,
    containerRef: notificationDropdownRef,
  });

  // Memoize event handlers
  const handleSideNavToggle = useCallback(() => {
    toggleStateValue({ key: "isSideNavClose" });
  }, [toggleStateValue]);

  const handleLogout = useCallback(() => {
    trackEvent("browse", {
      category: "User Info",
      entity: "logout",
      action: "click",
      location: analyticsLocation,
      ...getTrackUserContext(),
    });
    const redirectParam = encodeURIComponent(window.location.origin);
    window.location.href = `${
      process.env.NX_APP_LOGOUT_URL || ""
    }?redirect=${redirectParam}`;
  }, [analyticsLocation]);

  const handleNotificationClickOutside = useCallback(() => {
    setShowNotificationDropdown(false);
  }, []);

  const handleSeeAllNotifications = useCallback(() => {
    setShowNotificationDropdown(false);
    setModalOpen(true);
  }, []);

  const handleRefresh = useCallback(() => {
    navigate(window.location.pathname, { replace: true });
  }, [navigate]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setInitialNotificationTab(undefined);
    deeplinkProcessedRef.current = false;
  }, []);

  const handleSettingsClick = useCallback(() => {
    navigate("/setting");
  }, [navigate]);

  const handleUserDetailsClick = useCallback(() => {
    trackEvent("browse", {
      category: "User Info",
      entity: "profile",
      action: "click",
      location: analyticsLocation,
      ...getTrackUserContext(),
    });
    const accountsBaseUrl = import.meta.env.VITE_ACCOUNTS || "";
    const meUrl = `${accountsBaseUrl}/me`;
    window.open(meUrl, "_blank");
    // refreshCurrentUser();
  }, [analyticsLocation]);

  const handleAppLauncherClick = useCallback(
    (app: (typeof APP_LAUNCHER_CONFIG)[0]) => {
      trackEvent("browse", {
        category: "App Launcher",
        entity: app.label,
        action: "click",
        location: analyticsLocation,
        ...getTrackUserContext(),
      });
      if (app.id === "denmark") {
        setDenmarkPriorityOpen(true);
      } else if (app.id === "discover" || app.id === "settings") {
        navigate(app.path);
      } else {
        window.open(app.path, "_blank");
      }
    },
    [navigate, analyticsLocation]
  );

  useClickOutside(notificationRef, handleNotificationClickOutside);

  // Extract constants outside component (moved to top level for better performance)
  const navConf = {
    topNav: true,
    refresh: false,
    sideNav: false,
    icon: true,
    label: true,
  };
  const navTabs = [
    {
      label: "Content",
      to: "/media/video",
      icon: TvMinimalPlay,
      alias: "/media",
    },
    { label: "Drive", to: "/drive/video", icon: Archive, alias: "/drive" },
    { label: "Discover", to: "/discover", icon: Binoculars, alias: "/disc" },
  ];

  return (
    <>
      <header className="mainHeader d-flex justify-content-between align-items-center p-2 z-3">
        <div className="d-flex align-items-center">
          <div className="d-flex align-items-center ms-2 pe-3">
            {navConf.sideNav && (
              <button
                className="btn btn-link p-0 mx-2"
                onClick={handleSideNavToggle}
              >
                <h3>
                  <i className={`bi bi-list text-primary`}></i>
                </h3>
              </button>
            )}
            <Link to="/">
              <SlikeLogo className="bi h-logo" />
            </Link>
          </div>
          {navConf.topNav && (
            <ul className="nav me-3 pe-3 text-uppercase f-14">
              {navTabs.map((item) => {
                const isContentActive =
                  item.label === "Content"
                    ? location.pathname.startsWith("/media") ||
                      location.pathname === "/media"
                    : item.label === "Drive"
                      ? location.pathname.startsWith("/drive")
                      : item.label === "Discover"
                        ? location.pathname.startsWith("/discover") ||
                          location.pathname === "/discover" ||
                          location.pathname === item.alias
                        : item.to === location.pathname ||
                          item.alias === location.pathname;
                const acls = isContentActive ? "active" : "";
                const iconSize = navConf.label ? 16 : 20;
                return (
                  <li key={item.label} className="nav-item">
                    <Link
                      to={item.to}
                      className={`d-flex gap-2 nav-link ${acls}`}
                    >
                      {navConf.icon && (
                        <item.icon
                          size={iconSize}
                          className="no-fill svg-outline bi lh-16"
                        />
                      )}
                      {navConf.label && item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="d-flex align-items-center gap-4">
            {navConf.refresh && (
              <button className="btn btn-link p-0" onClick={handleRefresh}>
                <Tooltip content="Refresh" placement="bottom">
                  <RotateCw className="iconColor" />
                </Tooltip>
              </button>
            )}
          </div>
        </div>
        <HeaderSearch />
        <div className="d-flex align-items-center gap-3">
          <FileUploader
            emptyAssetDetails={emptyAssetDetails}
            emptyMediaDetails={emptyMediaDetails}
            currentMediaDetailsForEdit={currentMediaDetailsForEdit}
            getMedia={getMedia}
            updateIsClearFilter={updateIsClearFilter}
            updateIsFilter={updateIsFilter}
            bunchProductList={bunchProductList}
          />
          <div className="position-relative me-2" ref={notificationRef}>
            <motion.button
              onClick={handleSeeAllNotifications}
              className="btn-link p-0 position-relative"
            >
              <Bell className="iconColor" />
              {displayCount > 0 && (
                <span className="badge badge-tr rounded-pill bg-danger">
                  {displayCount > 10 ? "10+" : displayCount}
                </span>
              )}
            </motion.button>
          </div>
          {modalOpen && (
            <Modal
              isOpen={modalOpen}
              onClose={handleCloseModal}
              title="Notification centre"
              closeOnOutsideClick={true}
              width="800px"
              height="100%"
              otherPropertiesStyle={{
                margin: "0px",
                height: "100%",
                maxWidth: "100%",
              }}
            >
              <Notifications
                onClose={handleCloseModal}
                initialTab={initialNotificationTab}
              />
            </Modal>
          )}
          <Settings
            size={24}
            strokeWidth={2}
            className="bi no-fill svg-outline H"
            onClick={handleSettingsClick}
          />
          <Dropdown className="app-launcher">
            <Dropdown.Toggle
              variant="link"
              className="p-0 border-0 bg-transparent no-caret"
            >
              <AppIcon strokeWidth={2} className="bi svg-btn svg-fill" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="app-launcher-menu">
              <div className="row m-0 pb-2 gy-2" style={{ minWidth: "320px" }}>
                {APP_LAUNCHER_CONFIG.filter((app) => {
                  if (app.id === "settings") {
                    return hasCMSAccess;
                  }
                  return true;
                }).map((app) => {
                  const IconComponent = app.icon;
                  const isDenmarkDisabled =
                    app.id === "denmark" && !hasDenmarkAccess;
                  return (
                    <div key={app.id} className="col-4 p-1">
                      <Dropdown.Item
                        className="text-center p-2 d-flex flex-column align-items-center"
                        onClick={() =>
                          !isDenmarkDisabled && handleAppLauncherClick(app)
                        }
                        title={
                          isDenmarkDisabled
                            ? "No Denmark host configured"
                            : app.description
                        }
                        disabled={isDenmarkDisabled}
                      >
                        {app.id === "denmark" ? (
                          <DenmarkFavIcon
                            className="mb-2 iconColor"
                            style={{
                              width: "24px",
                              height: "24px",
                              opacity: isDenmarkDisabled ? 0.35 : 1,
                              filter: isDenmarkDisabled
                                ? "grayscale(100%)"
                                : "none",
                            }}
                          />
                        ) : (
                          <IconComponent
                            className="mb-2 iconColor"
                            style={{
                              width: "24px",
                              height: "24px",
                              opacity: isDenmarkDisabled ? 0.35 : 1,
                              filter: isDenmarkDisabled
                                ? "grayscale(100%)"
                                : "none",
                            }}
                          />
                        )}
                        <span>{app.label}</span>
                      </Dropdown.Item>
                    </div>
                  );
                })}
              </div>
            </Dropdown.Menu>
          </Dropdown>
          <div className="px-3 border-start">
            <Dropdown>
              <Dropdown.Toggle
                as="div"
                className="border-0 bg-transparent no-caret p-0"
                style={{ cursor: "pointer" }}
              >
                <ProfileIcon
                  className="rounded-circle bi profile"
                  style={{ width: "32px", height: "32px", opacity: "0.8" }}
                />
              </Dropdown.Toggle>
              <Dropdown.Menu align="end" style={{ minWidth: "150px" }}>
                <Dropdown.Item
                  className="text-capitalize"
                  onClick={handleUserDetailsClick}
                >
                  <CircleUser size={20} className="me-1" /> User Details
                </Dropdown.Item>
                {hasCMSAccess && (
                  <Dropdown.Item
                    className="text-capitalize py-2"
                    onClick={handleSettingsClick}
                  >
                    <Settings size={20} className="me-1" /> Settings
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  className="text-capitalize py-2"
                  onClick={handleLogout}
                >
                  <LogOut size={20} className="me-1" /> Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </header>
      <DenmarkPriorityModal
        isOpen={denmarkPriorityOpen}
        onClose={() => setDenmarkPriorityOpen(false)}
        defaultTeam={defaultTeam}
      />
    </>
  );
};

const mapState = (state: RootState) => ({
  singleCurrentUser: state.users.singleCurrentUser,
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
  notifications: state.app.notifications.list,
  bunchProductList: state.constants.bunchProductList.list,
  defaultTeam: state.constants.defaultTeam,
});

const mapDispatch = {
  toggleStateValue,
  emptyAssetDetails,
  emptyMediaDetails,
  getMedia,
  readNotification,
  updateIsClearFilter,
  refreshCurrentUser,
  updateIsFilter,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

export default connector(Header);
